/**
 * Quello che la testata e le schede di un evento dicono a colpo d'occhio:
 * quando si tiene, a che punto e', cosa manca per farlo partire.
 */

import type { GiornoEvento, ProgrammaEvento } from '@eventi/tipi.ts';
import { programmaPronto } from './programma';
import type { Evento } from './tipi';

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

type Data = { giorno: number; mese: number; anno: number };

/** 'AAAA-MM-GG' in numeri, senza passare dai fusi orari: e' gia' una data italiana. */
function leggiData(testo: string | undefined): Data | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(testo ?? '');
  if (!m) return null;
  const [anno, mese, giorno] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return mese >= 1 && mese <= 12 && giorno >= 1 && giorno <= 31 ? { giorno, mese, anno } : null;
}

const chiave = (d: Data) => d.anno * 10000 + d.mese * 100 + d.giorno;

/** Le date valide del programma, dalla prima all'ultima. */
function dateInOrdine(giorni: GiornoEvento[]): Data[] {
  return giorni
    .map((g) => leggiData(g.data))
    .filter((d): d is Data => d !== null)
    .sort((a, b) => chiave(a) - chiave(b));
}

/** "5 novembre 2026", "5–6 novembre 2026", "30 ottobre – 2 novembre 2026"; null se il programma non ha date. */
export function periodoEvento(giorni: GiornoEvento[]): string | null {
  const ordinate = dateInOrdine(giorni);
  if (ordinate.length === 0) return null;
  const da = ordinate[0];
  const a = ordinate[ordinate.length - 1];
  const intera = (d: Data) => `${d.giorno} ${MESI[d.mese - 1]} ${d.anno}`;
  if (chiave(da) === chiave(a)) return intera(da);
  if (da.anno !== a.anno) return `${intera(da)} – ${intera(a)}`;
  if (da.mese !== a.mese) return `${da.giorno} ${MESI[da.mese - 1]} – ${intera(a)}`;
  return `${da.giorno}–${a.giorno} ${MESI[a.mese - 1]} ${a.anno}`;
}

/** Giorno e mese breve del primo giorno, per la tessera del calendario ("5", "NOV"). */
export function tesseraData(giorni: GiornoEvento[]): { giorno: string; mese: string } | null {
  const prima = dateInOrdine(giorni)[0];
  if (!prima) return null;
  return { giorno: String(prima.giorno), mese: MESI[prima.mese - 1].slice(0, 3).toUpperCase() };
}

export type SchedaDaAprire = 'programma' | 'impostazioni';

export interface Mancanza {
  id: 'programma' | 'lista' | 'url_scelta' | 'etichetta_assegnata' | 'etichetta_acquisto';
  testo: string;
  perche: string;
  scheda: SchedaDaAprire;
}

/** Le cose da sistemare perche' l'evento funzioni, nell'ordine in cui conviene farle. */
export function cosaManca(
  evento: Pick<Evento, 'lista_id' | 'url_scelta' | 'etichetta_assegnata_id'> &
    Partial<Pick<Evento, 'prodotti_sito' | 'etichetta_acquisto_id'>>,
  programma: ProgrammaEvento,
): Mancanza[] {
  const mancanze: Mancanza[] = [];
  if (!programmaPronto(programma)) {
    mancanze.push({ id: 'programma', testo: 'Importa il programma', perche: "senza, non c'è niente da scegliere", scheda: 'programma' });
  }
  if (!evento.lista_id) {
    mancanze.push({ id: 'lista', testo: 'Scegli la lista dei partecipanti', perche: 'chi è nella lista riceve il suo link', scheda: 'impostazioni' });
  }
  if (!evento.url_scelta) {
    mancanze.push({
      id: 'url_scelta',
      testo: "Indica l'indirizzo della pagina di scelta",
      perche: 'è dove porta il link personale nelle email',
      scheda: 'impostazioni',
    });
  }
  if (!evento.etichetta_assegnata_id) {
    mancanze.push({
      id: 'etichetta_assegnata',
      testo: "Scegli l'etichetta «percorso assegnato»",
      perche: "senza, confermando non parte l'email con il percorso",
      scheda: 'impostazioni',
    });
  }
  if ((evento.prodotti_sito ?? []).length > 0 && !evento.etichetta_acquisto_id) {
    mancanze.push({
      id: 'etichetta_acquisto',
      testo: "Scegli l'etichetta «biglietti acquistati»",
      perche: "senza, chi compra sul sito non riceve l'email per registrare i biglietti",
      scheda: 'impostazioni',
    });
  }
  return mancanze;
}

/** Quello che ogni scheda dice di se' nella barra delle schede. */
export interface RiassuntoScheda {
  testo: string;
  /** Da 0 a 1, per la barretta; assente se non ha senso. */
  avanzamento?: number;
  /** Fatto tutto: la scheda mostra la spunta verde. */
  fatto: boolean;
  /** C'e' qualcosa da sistemare: il testo e' in giallo. */
  avviso: boolean;
}

export function riassuntoSchede(
  programma: ProgrammaEvento,
  conti: { totale: number; da_scegliere: number; bozza: number; confermata: number },
  daCompletareInImpostazioni: number,
): Record<'programma' | 'richieste' | 'assegnazioni' | 'impostazioni', RiassuntoScheda> {
  const incontri = programma.sessioni.filter((s) => s.sceglibile).length;
  const hannoScelto = conti.totale - conti.da_scegliere;
  const nessuno: RiassuntoScheda = { testo: 'Nessun partecipante', fatto: false, avviso: false };
  return {
    programma: programmaPronto(programma)
      ? { testo: `${incontri} incontri da scegliere`, fatto: true, avviso: false }
      : { testo: 'Da importare', fatto: false, avviso: true },
    richieste:
      conti.totale === 0
        ? nessuno
        : { testo: `${hannoScelto} su ${conti.totale} hanno scelto`, avanzamento: hannoScelto / conti.totale, fatto: hannoScelto === conti.totale, avviso: false },
    assegnazioni:
      conti.totale === 0
        ? nessuno
        : {
            testo: `${conti.confermata} su ${conti.totale} confermati` + (conti.bozza > 0 ? ` · ${conti.bozza} in bozza` : ''),
            avanzamento: conti.confermata / conti.totale,
            fatto: conti.confermata === conti.totale,
            avviso: false,
          },
    impostazioni:
      daCompletareInImpostazioni === 0
        ? { testo: 'Tutto a posto', fatto: true, avviso: false }
        : { testo: daCompletareInImpostazioni === 1 ? '1 cosa da completare' : `${daCompletareInImpostazioni} cose da completare`, fatto: false, avviso: true },
  };
}
